import { useQuery } from '@tanstack/react-query';
import { debounce, omitBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { FilterToolbarItemId } from 'src/components/Filters';
import { FilterDialog } from 'src/components/FilterToolbar/FilterDialog';
import * as filterToolbarStyles from 'src/components/FilterToolbar/FilterToolbar.css';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  Content,
  FormatContent,
  useContent,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { PosSearchBox } from 'src/core/POS/PosSearchBox';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Button, Popover, Stack } from 'src/core/ui';
import { PillList } from 'src/core/ui/PillList';
import { PillItemProps } from 'src/core/ui/PillList/PillItem';
import { RequestEventDialog } from 'src/dialogs/RequestEventDialog';
import { usePurchaseOrderSettings } from 'src/hooks/api/usePurchaseOrderSettings';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  ArtistIcon,
  FilterIcon,
  IconsFill,
  LocationOutlineIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  EVENT_SORT_TO_CID,
  YES_NO_ENUM_FILTER_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { EventDateRangePresetNames } from 'src/utils/dateTimeUtils';
import {
  FromYesNoEnum,
  getAppliedFilterCounts,
  getEventSort,
  getEventUiSort,
  ToYesNoEnum,
} from 'src/utils/eventQueryUtils';
import {
  getPerformerAndVenueForEvent,
  sortEventsByDate,
  sortEventsByName,
} from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  CatalogClient,
  CatalogResults,
  DateTimeRange,
  Event,
  EventSort,
  EventWithData,
  Feature,
  Performer,
  Venue,
} from 'src/WebApiController';

import * as styles from './EventSearch.css';
import { EventSearchInputBoxContainer } from './EventSearch.styled';
import {
  EventSearchFilterQuery,
  EventSearchProps,
  SearchEventsQuery,
} from './EventSearch.types';
import {
  deriveScopedPerformersAndVenues,
  filterScopedEvents,
  isParkingEvent,
  isPendingRejectedEvent,
  ScopedEventFilters,
} from './EventSearch.utils';
import { EventSearchDropdownResult } from './EventSearchDropdownResult';
import { EventSearchItem } from './EventSearchItem';

const DEFAULT_EVENT_SEARCH_FILTER_QUERY: EventSearchFilterQuery = {
  showParkingTickets: true,
  showPendingRejectedEvents: true,
};

/**
 * Checks if the search text looks like a viagogo event ID (a positive integer).
 * Used by the EventSearchByID feature to route searches to getCatalogFull
 * instead of the text-based search endpoint.
 */
const isEventIdSearch = (text: string | undefined | null): boolean => {
  if (!text) return false;
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) && trimmed.length > 0;
};

export function EventSearch({
  selectedEvents,
  eventsToSearchFrom,
  showEventsToSearchFromOnEmptySearch,
  onEventsSelect,
  searchTextPrefill,
  numEventsSelected,
  onDeleteNumEventsSelected,
  maxNumOfResults,
  allowSearchByVenueConfig,
  searchPlaceHolder,
  isSingleSelect,
  timeFrame,
  defaultSearchEventsQuery,
  defaultDateFilter,
  venueConfigId,
  maxSelections,
  eventsToExcludeViagogoIds,
  requestCount = 1,
  persistSelected,
  onBeforeRequestEvent,
  deriveScopedEntityResults,
  catalogData: ambientCatalogData,
  disableParkingTicketsFilter,
  alwaysShowDropdownSections,
  renderEventItemTrailing,
}: EventSearchProps) {
  const hasUpdatePurchaseCreationSort = useUserHasFeature(
    Feature.UpdatePurchaseCreationSort
  );
  const hasEventSearchByID = useUserHasFeature(Feature.EventSearchByID);
  // Safe: useUserHasFeature is synchronous (reads from AppContext via useMemo),
  // so hasUpdatePurchaseCreationSort won't flip after mount.
  const [availableEvents, setAvailableEvents] = useState<
    Map<string, React.ReactNode> | Record<string, React.ReactNode>
  >(hasUpdatePurchaseCreationSort ? new Map() : {});

  const { data: purchaseOrderSettings } = usePurchaseOrderSettings();
  const excludeParkingEventsFromEventSearch =
    purchaseOrderSettings?.excludeParkingEventsFromEventSearch ?? false;

  const defaultEventSearchFilterQuery: EventSearchFilterQuery = useMemo(
    () => ({
      showParkingTickets: disableParkingTicketsFilter
        ? false
        : !excludeParkingEventsFromEventSearch,
      showPendingRejectedEvents: true,
    }),
    [disableParkingTicketsFilter, excludeParkingEventsFromEventSearch]
  );

  // Baseline used for the applied-filter badge and "Reset all": when the
  // parking filter is pinned to No, No is the neutral state, not a filter
  const resetFilterQueryBaseline: EventSearchFilterQuery = useMemo(
    () =>
      disableParkingTicketsFilter
        ? { ...DEFAULT_EVENT_SEARCH_FILTER_QUERY, showParkingTickets: false }
        : DEFAULT_EVENT_SEARCH_FILTER_QUERY,
    [disableParkingTicketsFilter]
  );

  const [tempFilterQuery, setTempFilterQuery] =
    useState<EventSearchFilterQuery>(defaultEventSearchFilterQuery);
  const [filterQuery, setFilterQuery] = useState<EventSearchFilterQuery>(
    defaultEventSearchFilterQuery
  );

  useEffect(() => {
    setTempFilterQuery(defaultEventSearchFilterQuery);
    setFilterQuery(defaultEventSearchFilterQuery);
  }, [defaultEventSearchFilterQuery]);

  const [dateFilter, setDateFilter] = useState<
    DateTimeRange | null | undefined
  >(defaultDateFilter);

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const toggleSelectAll = () => {
    const events =
      (filteredSearchEvents?.events &&
        Object.values(filteredSearchEvents.events)) ??
      [];
    onEventsSelect(
      events.map((eventWithData) => {
        const { event } = eventWithData;
        const catalog = filteredSearchEvents;
        const { performer, venue } = getPerformerAndVenueForEvent(
          eventWithData.event,
          catalog
        );
        return {
          event,
          isSelected: isAllSelected ? false : true,
          performer,
          venue,
        };
      })
    );
    setIsAllSelected((previous) => !previous);
  };

  const filterAppliedCounts = getAppliedFilterCounts(
    tempFilterQuery,
    resetFilterQueryBaseline
  );
  const onResetAll = () => {
    setTempFilterQuery(resetFilterQueryBaseline);
    setFilterQuery(resetFilterQueryBaseline);
    // The event date filter lives in its own state — reset it too
    setDateFilter(defaultDateFilter);
  };
  const onSubmitFilter = () => {
    setFilterQuery(tempFilterQuery);
  };

  const numEventsSelectedText = useFormattedContent(
    FormatContentId.EventsSelected,
    numEventsSelected.toString()
  );
  const defaultSearchPlaceholderContentId = hasEventSearchByID
    ? ContentId.SearchEventPlaceholderTextWithEventId
    : ContentId.SearchEventPlaceholderText;
  const searchPlaceholderText = useContent(
    searchPlaceHolder ?? defaultSearchPlaceholderContentId
  );
  const { activeAccountWebClientConfig } = useAppContext();
  // Scoped autocomplete results embed performer/venue entries derived from
  // the ambient catalog, so refetch when its entity maps change size (e.g.
  // the entity data arriving after the first search was performed)
  const ambientEntityResultCount =
    Object.keys(ambientCatalogData?.performers ?? {}).length +
    Object.keys(ambientCatalogData?.venues ?? {}).length;
  const isMobile = useMatchMedia('mobile');
  const { showErrorDialog } = useClientLogContext();
  const [searchText, setSearchText] = useState<string>(searchTextPrefill ?? '');
  const [searchTextDebounced, setSearchTextDebounced] = useState<string>();
  const [searchEventsQuery, setSearchEventQuery] = useState<
    SearchEventsQuery | undefined
  >(defaultSearchEventsQuery);
  const [eventSortQuery, setEventSortQuery] = useState<{
    sortBy: EventSort | null;
    isSortDescending: boolean | null;
  }>({ sortBy: null, isSortDescending: false });

  const searchEventsQueryRef = useRef(searchEventsQuery);
  searchEventsQueryRef.current = searchEventsQuery;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchTextChangedDebounced = useCallback(
    debounce((s: string | undefined) => {
      s = s?.trim();
      if (s !== searchTextDebounced) {
        setSearchTextDebounced(s);

        if (
          searchEventsQueryRef.current &&
          (!s ||
            searchEventsQueryRef.current.performerId ||
            searchEventsQueryRef.current.venueId)
        ) {
          // If we have a filterQuery, and we s is null (being reset)
          // or we have either performerId or venueId set
          // we want to invoke a search-event based on s
          setSearchEventQuery({
            ...searchEventsQueryRef.current,
            searchText: s || null,
          });
        }
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (searchTextPrefill) {
      onSearchTextChangedDebounced(searchTextPrefill);
    }
  }, [onSearchTextChangedDebounced, searchTextPrefill]);

  const getStaticResult = useCallback(
    async (searchText: string, filters?: ScopedEventFilters) => {
      if (eventsToSearchFrom) {
        const scopedCatalog =
          deriveScopedEntityResults && filters != null
            ? ambientCatalogData
            : undefined;
        const eventResults = filterScopedEvents(
          eventsToSearchFrom,
          {
            searchText,
            performerId: filters?.performerId ?? null,
            venueId: filters?.venueId ?? null,
            excludeParkingEvents: filters?.excludeParkingEvents,
            excludePendingRejectedEvents: filters?.excludePendingRejectedEvents,
          },
          scopedCatalog
        );
        const scopedEntities = deriveScopedEntityResults
          ? deriveScopedPerformersAndVenues(
              filters?.excludeParkingEvents ||
                filters?.excludePendingRejectedEvents
                ? eventsToSearchFrom.filter(
                    (e) =>
                      !(
                        filters?.excludeParkingEvents &&
                        isParkingEvent(e.event.name)
                      ) &&
                      !(
                        filters?.excludePendingRejectedEvents &&
                        isPendingRejectedEvent(e.event)
                      )
                  )
                : eventsToSearchFrom,
              searchText,
              ambientCatalogData
            )
          : { performers: {}, venues: {} };
        return {
          events: eventResults.reduce<Record<string, EventWithData>>((r, e) => {
            r[e.event.virtId] = e;
            return r;
          }, {}),
          venueCfgs: allowSearchByVenueConfig
            ? eventResults
                .filter((e) => e.event.venCfgId)
                .reduce<Record<string, Event[]>>((r, e) => {
                  const key = e.event.venCfgId!.toString();
                  if (r[key]) {
                    r[key] = [...r[key], e.event];
                  } else {
                    r[key] = [e.event];
                  }
                  return r;
                }, {})
            : {},
          performers: scopedEntities.performers,
          venues: scopedEntities.venues,
        } as CatalogResults & { venueCfgs: Record<string, Event[]> };
      }
    },
    [
      allowSearchByVenueConfig,
      ambientCatalogData,
      deriveScopedEntityResults,
      eventsToSearchFrom,
    ]
  );

  const searchEvents = useQuery({
    queryKey: [
      'searchEvents',
      searchEventsQuery,
      hasEventSearchByID,
      // Scoped results are derived client-side and depend on the parking and
      // pending/rejected filter state; include them so toggling re-derives
      // (undefined unscoped)
      eventsToSearchFrom != null ? filterQuery.showParkingTickets : undefined,
      eventsToSearchFrom != null
        ? filterQuery.showPendingRejectedEvents
        : undefined,
      eventsToSearchFrom != null ? ambientEntityResultCount : undefined,
    ],
    queryFn: () => {
      if (activeAccountWebClientConfig.activeAccountId == null) {
        return null;
      }

      if (
        !searchEventsQuery?.searchText &&
        !searchEventsQuery?.performerId &&
        !searchEventsQuery?.venueId &&
        !searchEventsQuery?.event &&
        !searchEventsQuery?.catalog
      ) {
        if (eventsToSearchFrom != null && showEventsToSearchFromOnEmptySearch) {
          return getStaticResult('', {
            excludeParkingEvents: !filterQuery.showParkingTickets,
            excludePendingRejectedEvents:
              !filterQuery.showPendingRejectedEvents,
          });
        }
        // If all of these are nulls, return nothing
        return null;
      }

      if (eventsToSearchFrom != null) {
        // Single event picked from the autocomplete dropdown: show just that
        // event, mirroring the unscoped purchase-flow behavior
        if (searchEventsQuery.event != null) {
          const selectedEvent = eventsToSearchFrom.find(
            (e) => e.event.virtId === searchEventsQuery.event!.virtId
          );
          return selectedEvent
            ? ({
                events: { [selectedEvent.event.virtId]: selectedEvent },
                performers: searchEventsQuery.performer?.viagId
                  ? {
                      [searchEventsQuery.performer?.viagId]:
                        searchEventsQuery.performer,
                    }
                  : {},
                venues: searchEventsQuery.venue?.viagId
                  ? {
                      [searchEventsQuery.venue?.viagId]:
                        searchEventsQuery.venue,
                    }
                  : {},
              } as CatalogResults)
            : null;
        }
        return getStaticResult(searchEventsQuery?.searchText ?? '', {
          performerId: searchEventsQuery?.performerId ?? null,
          venueId: searchEventsQuery?.venueId ?? null,
          excludeParkingEvents: !filterQuery.showParkingTickets,
          excludePendingRejectedEvents: !filterQuery.showPendingRejectedEvents,
        });
      }

      return tryInvokeApi(
        async () => {
          if (searchEventsQuery.event) {
            return {
              events: {
                [searchEventsQuery.event.virtId]: {
                  event: searchEventsQuery.event,
                  counts: {},
                  entities: {},
                } as EventWithData,
              },
              performers: searchEventsQuery.performer?.viagId
                ? {
                    [searchEventsQuery.performer?.viagId]:
                      searchEventsQuery.performer,
                  }
                : {},
              venues: searchEventsQuery.venue?.viagId
                ? {
                    [searchEventsQuery.venue?.viagId]: searchEventsQuery.venue,
                  }
                : {},
            } as CatalogResults;
          }

          if (searchEventsQuery.catalog) {
            return searchEventsQuery.catalog;
          }

          // When EventSearchByID is enabled and the search text is a numeric event ID,
          // look up the event directly by its viagogo ID
          if (
            hasEventSearchByID &&
            isEventIdSearch(searchEventsQuery.searchText)
          ) {
            return new CatalogClient(
              activeAccountWebClientConfig
            ).getCatalogFull([searchEventsQuery.searchText!.trim()]);
          }

          return new CatalogClient(activeAccountWebClientConfig).searchEvents(
            searchEventsQuery
          );
        },
        (error) => {
          showErrorDialog('CatalogClient.searchEvents', error, {
            trackErrorData: searchEventsQuery,
          });
        }
      );
    },
    refetchOnWindowFocus: false,
  });

  const filteredSearchEvents = useMemo<typeof searchEvents.data>(() => {
    const catalog = searchEvents.data;
    if (!catalog) {
      return null;
    }
    const filteredEvents: typeof catalog.events = {};
    for (const [key, event] of Object.entries(catalog.events)) {
      if (
        !!event.event.viagId &&
        eventsToExcludeViagogoIds?.includes(event.event.viagId)
      ) {
        continue;
      }
      if (
        dateFilter?.start &&
        (!event.event.dates.start ||
          new Date(event.event.dates.start).valueOf() <
            new Date(dateFilter?.start).valueOf())
      ) {
        continue;
      }
      if (
        dateFilter?.end &&
        (!event.event.dates.start ||
          new Date(event.event.dates.start).valueOf() >
            new Date(dateFilter?.end).valueOf())
      ) {
        continue;
      }
      if (venueConfigId && event.event.venCfgId !== venueConfigId) {
        continue;
      }
      // filter out parking tickets
      if (
        !filterQuery.showParkingTickets &&
        // TODO we should check the event type here instead
        // currently we don't have that data in the response
        event.event.name.toLowerCase().startsWith('parking passes only')
      ) {
        continue;
      }

      if (!filterQuery.showPendingRejectedEvents) {
        const isRejected =
          event.event.viagId == null && event.event.mapRejRsnId != null;
        const isPending =
          event.event.viagId == null && event.event.mapRejRsnId == null;
        if (isRejected || isPending) {
          continue;
        }
      }

      filteredEvents[key] = event;
    }
    return {
      ...catalog,
      events: filteredEvents,
    };
  }, [
    searchEvents.data,
    eventsToExcludeViagogoIds,
    dateFilter?.start,
    dateFilter?.end,
    venueConfigId,
    filterQuery.showParkingTickets,
    filterQuery.showPendingRejectedEvents,
  ]);

  const shouldQuery = Boolean(
    searchTextDebounced &&
    !(searchEventsQuery?.performerId && searchEventsQuery?.venueId)
  );

  const autoCompleteResultQuery = useQuery({
    queryKey: [
      'getSearchEventsAutoCompleteResult',
      searchTextDebounced,
      timeFrame,
      maxNumOfResults ?? 10,
      hasEventSearchByID,
      eventsToSearchFrom != null ? filterQuery.showParkingTickets : undefined,
      eventsToSearchFrom != null
        ? filterQuery.showPendingRejectedEvents
        : undefined,
      eventsToSearchFrom != null ? ambientEntityResultCount : undefined,
    ],
    queryFn: () => {
      return tryInvokeApi(
        async () => {
          if (!shouldQuery) {
            return null;
          }

          if (eventsToSearchFrom != null) {
            return getStaticResult(searchTextDebounced!, {
              excludeParkingEvents: !filterQuery.showParkingTickets,
              excludePendingRejectedEvents:
                !filterQuery.showPendingRejectedEvents,
            });
          }

          // When EventSearchByID is enabled and the input looks like a numeric event ID,
          // look up the event directly by its viagogo ID instead of text search
          if (hasEventSearchByID && isEventIdSearch(searchTextDebounced)) {
            const r = await new CatalogClient(
              activeAccountWebClientConfig
            ).getCatalogFull([searchTextDebounced!.trim()]);

            return r as CatalogResults & { venueCfgs: Record<string, Event[]> };
          }

          const r = await new CatalogClient(
            activeAccountWebClientConfig
          ).getSearchEventsAutoCompleteResult(
            searchTextDebounced,
            maxNumOfResults ?? 10, // we want 10 at least so we can get the best result up top
            timeFrame
          );

          return r as CatalogResults & { venueCfgs: Record<string, Event[]> };
        },
        (error) => {
          showErrorDialog(
            'CatalogClient.getSearchEventsAutoCompleteResult',
            error,
            { trackErrorData: { searchTextDebounced } }
          );
        }
      );
    },
    refetchOnWindowFocus: false,
    enabled: shouldQuery,
  });

  const updatedEvents = useMemo(() => {
    let events =
      (filteredSearchEvents?.events &&
        Object.values(filteredSearchEvents.events)) ??
      [];

    if (!events || events.length === 0) {
      return hasUpdatePurchaseCreationSort
        ? new Map<string, React.ReactNode>()
        : ({} as Record<string, React.ReactNode>);
    }

    if (eventSortQuery.sortBy === EventSort.MainDisplay) {
      events = sortEventsByName(events, eventSortQuery.isSortDescending);
    } else {
      events = sortEventsByDate(events, eventSortQuery.isSortDescending);
    }

    if (hasUpdatePurchaseCreationSort) {
      const result = new Map<string, React.ReactNode>();
      events.forEach((event) => {
        if (event.event.virtId) {
          const isSelected = selectedEvents?.find(
            (seid) => seid === event.event.virtId
          );
          const isItemDisabled =
            maxSelections != null &&
            (selectedEvents?.length ?? 0) >= maxSelections &&
            !isSelected;
          result.set(
            event.event.virtId,
            <EventSearchItem
              event={event.event}
              isSelected={Boolean(isSelected)}
              catalog={filteredSearchEvents!}
              onChecked={(
                event: Event,
                isSelected: boolean,
                performer?: Performer | null,
                venue?: Venue | null
              ) => {
                onEventsSelect([{ event, isSelected, performer, venue }]);
              }}
              isSingleSelect={isSingleSelect}
              isDisabled={isItemDisabled}
              trailing={renderEventItemTrailing?.(
                event.event,
                Boolean(isSelected)
              )}
            />
          );
        }
      });
      return result;
    }

    // Legacy path (feature flag off): uses Record which loses sort order
    const result = {} as Record<string, React.ReactNode>;
    events.forEach((event) => {
      if (event.event.virtId) {
        const isSelected = selectedEvents?.find(
          (seid) => seid === event.event.virtId
        );
        const isItemDisabled =
          maxSelections != null &&
          (selectedEvents?.length ?? 0) >= maxSelections &&
          !isSelected;
        result[event.event.virtId] = (
          <EventSearchItem
            event={event.event}
            isSelected={Boolean(isSelected)}
            catalog={filteredSearchEvents!}
            onChecked={(
              event: Event,
              isSelected: boolean,
              performer?: Performer | null,
              venue?: Venue | null
            ) => {
              onEventsSelect([{ event, isSelected, performer, venue }]);
            }}
            isSingleSelect={isSingleSelect}
            isDisabled={isItemDisabled}
            trailing={renderEventItemTrailing?.(
              event.event,
              Boolean(isSelected)
            )}
          />
        );
      }
    });
    return result;
  }, [
    eventSortQuery.isSortDescending,
    eventSortQuery.sortBy,
    filteredSearchEvents,
    hasUpdatePurchaseCreationSort,
    isSingleSelect,
    maxSelections,
    onEventsSelect,
    renderEventItemTrailing,
    selectedEvents,
  ]);

  useEffect(() => {
    if (persistSelected) {
      setAvailableEvents((prev) => {
        if (hasUpdatePurchaseCreationSort) {
          const prevMap = prev as Map<string, React.ReactNode>;
          const eventsMap = updatedEvents as Map<string, React.ReactNode>;
          const updated = new Map<string, React.ReactNode>();
          for (const [key, value] of prevMap) {
            if (selectedEvents?.includes(key)) {
              updated.set(key, value);
            }
          }
          for (const [virtId, eventDisplay] of eventsMap) {
            updated.set(virtId, eventDisplay);
          }
          return updated;
        }
        // Legacy path
        const prevRecord = prev as Record<string, React.ReactNode>;
        const eventsRecord = updatedEvents as Record<string, React.ReactNode>;
        const updated = omitBy(
          prevRecord,
          (_, key) => !selectedEvents?.includes(key)
        );
        for (const [virtId, eventDisplay] of Object.entries(eventsRecord)) {
          Object.assign(updated, { [virtId]: eventDisplay });
        }
        return updated;
      });
    }
  }, [
    hasUpdatePurchaseCreationSort,
    persistSelected,
    selectedEvents,
    updatedEvents,
  ]);

  const eventsToDisplay = useMemo(() => {
    if (persistSelected) {
      if (hasUpdatePurchaseCreationSort) {
        return [...(availableEvents as Map<string, React.ReactNode>).values()];
      }
      return Object.values(availableEvents as Record<string, React.ReactNode>);
    }
    if (hasUpdatePurchaseCreationSort) {
      return [...(updatedEvents as Map<string, React.ReactNode>).values()];
    }
    return Object.values(updatedEvents as Record<string, React.ReactNode>);
  }, [
    availableEvents,
    hasUpdatePurchaseCreationSort,
    persistSelected,
    updatedEvents,
  ]);

  const onSearchItemSelected = useCallback(
    async (
      searchText: string | null,
      event?: Event | null,
      performer?: Performer | null,
      venue?: Venue | null,
      events?: Event[]
    ) => {
      setSearchText(searchText || '');
      setSearchTextDebounced(undefined);
      if (event != null) {
        setSearchEventQuery({
          searchText: null,
          performerId: null,
          venueId: null,
          performer: performer,
          venue: venue,
          event: event,
          timeFrame: timeFrame ?? null,
        });
        onEventsSelect([{ event, isSelected: true, performer, venue }]);
      } else if (events != null) {
        // venue config selected
        onEventsSelect(
          events.map((ev) => ({
            event: ev,
            isSelected: true,
            performer: null,
            venue: null,
          }))
        );
      } else {
        setSearchEventQuery({
          searchText: searchText || performer?.name || '',
          performer:
            performer ??
            (searchEventsQuery?.event ? null : searchEventsQuery?.performer),
          performerId:
            performer?.viagId ?? searchEventsQuery?.performer?.viagId ?? null,
          venue:
            venue ||
            (searchEventsQuery?.event ? null : searchEventsQuery?.venue),
          venueId: venue?.viagId ?? searchEventsQuery?.venue?.viagId ?? null,
          timeFrame: timeFrame ?? null,
        });
      }
    },
    [
      onEventsSelect,
      searchEventsQuery?.event,
      searchEventsQuery?.performer,
      searchEventsQuery?.venue,
      timeFrame,
    ]
  );

  const hasResult = Boolean(
    (searchEventsQuery || showEventsToSearchFromOnEmptySearch) &&
    eventsToDisplay?.length
  );
  const pills = useMemo(() => {
    const pills = [] as PillItemProps[];
    if (searchEventsQuery?.performer && searchEventsQuery.event == null) {
      pills.push({
        value: 'performer',
        display: searchEventsQuery.performer.name,
        icon: <ArtistIcon size={vars.iconSize.m} />,
        onDelete: () => {
          if (searchEventsQuery) {
            setSearchEventQuery({
              ...searchEventsQuery,
              performer: undefined,
              performerId: null,
            });
          }
        },
      });
    }

    if (searchEventsQuery?.venue && searchEventsQuery.event == null) {
      pills.push({
        value: 'venue',
        display: searchEventsQuery.venue.name,
        icon: <LocationOutlineIcon size={vars.iconSize.m} />,
        onDelete: () => {
          if (searchEventsQuery) {
            searchEventsQuery.venue = undefined;
            searchEventsQuery.venueId = null;
            setSearchEventQuery({ ...searchEventsQuery! });
          }
        },
      });
    }

    if (numEventsSelected && numEventsSelected > 0) {
      pills.push({
        value: 'numEventsSelected',
        display: numEventsSelectedText,
        onDelete: onDeleteNumEventsSelected,
      });
    }

    return pills;
  }, [
    searchEventsQuery,
    numEventsSelected,
    numEventsSelectedText,
    onDeleteNumEventsSelected,
  ]);

  const requestEventDialog = useBasicDialog();

  const onSaveRequestEvent = useCallback(
    async (results: CatalogResults | null) => {
      if (!results) {
        return;
      }

      // Set the search query so it set the event to be displayed
      setSearchEventQuery({
        ...searchEventsQuery,
        searchText: null,
        event: null,
        venueId: null,
        venue: null,
        performer: null,
        performerId: null,
        catalog: results,
        timeFrame: timeFrame ?? null,
      });

      // Auto-select all newly requested events so the season form is ready
      // to save without requiring the user to manually check each one.
      const newlyRequested = Object.values(results.events).map(
        (eventWithData) => {
          const { performer, venue } = getPerformerAndVenueForEvent(
            eventWithData.event,
            results
          );
          return {
            event: eventWithData.event,
            isSelected: true,
            performer,
            venue,
          };
        }
      );
      if (newlyRequested.length > 0) {
        onEventsSelect(newlyRequested);
      }

      requestEventDialog.closeDialog();
    },
    [onEventsSelect, requestEventDialog, searchEventsQuery, timeFrame]
  );

  const openDropdown =
    searchText &&
    searchTextDebounced &&
    !(searchEventsQuery?.performerId && searchEventsQuery?.venueId);

  return (
    <>
      <Stack
        direction="column"
        gap="l"
        className={styles.eventSearchContainer}
        height="full"
      >
        <Stack direction="column" gap="s">
          <div className={styles.eventSearchInputContainer}>
            <EventSearchInputBoxContainer>
              <PosDropdown
                align="start"
                rootProps={{
                  open: Boolean(openDropdown),
                }}
                trigger={
                  <div
                    className={styles.eventSearchInputSearchBox}
                    data-testid="event-search-input"
                  >
                    <PosSearchBox
                      value={searchText}
                      placeholder={searchPlaceholderText}
                      maxWidth={'100%'}
                      onSearchChange={(value) => {
                        setSearchText(value);
                        onSearchTextChangedDebounced(value);
                      }}
                      postfixDisplay={
                        hasResult && (
                          <Button
                            variant="textPlain"
                            textColor="strong"
                            shape="none"
                            size="unset"
                            onClick={() => {
                              setSearchEventQuery(undefined);
                              setEventSortQuery({
                                sortBy: null,
                                isSortDescending: true,
                              });
                              setSearchText('');
                            }}
                          >
                            <Content id={ContentId.ResetAll} />
                          </Button>
                        )
                      }
                    />
                  </div>
                }
                onOpenAutoFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onCloseAutoFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {openDropdown ? (
                  <EventSearchDropdownResult
                    searchText={searchTextDebounced}
                    isSearching={autoCompleteResultQuery.isFetching}
                    catalogResults={autoCompleteResultQuery.data}
                    onSearchItemSelected={onSearchItemSelected}
                    maxNumOfResults={maxNumOfResults}
                    alwaysShowDropdownSections={alwaysShowDropdownSections}
                  />
                ) : undefined}
              </PosDropdown>
            </EventSearchInputBoxContainer>

            <div className={styles.eventSearchSortBoxContainer}>
              <Popover.Root
                onOpenChange={(open) => {
                  // Commit the pending filter edits when the popover closes
                  // (Apply, X, or clicking outside) — not when it opens
                  if (!open) {
                    onSubmitFilter();
                  }
                }}
              >
                <Popover.Trigger asChild>
                  <div className={filterToolbarStyles.filterToolbarIcon}>
                    <FilterIcon
                      title="filter"
                      fill={
                        filterAppliedCounts
                          ? IconsFill.textBrand
                          : IconsFill.textPrimary
                      }
                      withHoverEffect
                    />
                    {Boolean(filterAppliedCounts) && (
                      <div className={filterToolbarStyles.filterCount}>
                        {filterAppliedCounts}
                      </div>
                    )}
                  </div>
                </Popover.Trigger>
                <Popover.Content
                  align="start"
                  onInteractOutside={(event) => {
                    // Dropdowns inside the popover (Radix Select/Popover)
                    // portal their content outside this popover's DOM tree,
                    // so Radix treats clicks on them as outside interactions
                    // and dismisses the popover mid-click — swallowing clicks
                    // on the popover's own controls (e.g. Reset all) and
                    // re-applying stale temp filters on the unexpected close.
                    // Treat portaled floating layers as inside; only Apply,
                    // X, or a genuine outside click closes the popover.
                    const target = event.detail.originalEvent.target;
                    if (
                      target instanceof Element &&
                      target.closest('[data-radix-popper-content-wrapper]')
                    ) {
                      event.preventDefault();
                    }
                  }}
                  style={{
                    maxHeight: 'var(--radix-popover-content-available-height)',
                  }}
                >
                  <FilterDialog
                    hasFiltersApplied={true}
                    onResetAll={onResetAll}
                    onSubmitFilter={onSubmitFilter}
                    filters={[
                      {
                        items: [
                          {
                            filterId:
                              'showParkingTickets' as FilterToolbarItemId,
                            labelContentId: ContentId.ShowParkingTickets,
                            filterQueryKeys: [
                              'showParkingTickets',
                            ] as FilterToolbarItemId[],
                            filterItem: (
                              <PosEnumSelect
                                style={{ width: '100%' }}
                                disabled={disableParkingTicketsFilter}
                                value={ToYesNoEnum(
                                  tempFilterQuery.showParkingTickets
                                )}
                                valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                                onChange={(yesNoEnumValue) => {
                                  if (
                                    yesNoEnumValue !==
                                    ToYesNoEnum(
                                      tempFilterQuery.showParkingTickets
                                    )
                                  ) {
                                    setTempFilterQuery({
                                      ...tempFilterQuery,
                                      showParkingTickets:
                                        FromYesNoEnum(yesNoEnumValue),
                                    });
                                  }
                                }}
                              />
                            ),
                          },
                          {
                            filterId:
                              'showPendingRejectedEvents' as FilterToolbarItemId,
                            labelContentId: ContentId.ShowPendingRejectedEvents,
                            filterQueryKeys: [
                              'showPendingRejectedEvents',
                            ] as FilterToolbarItemId[],
                            filterItem: (
                              <PosEnumSelect
                                style={{ width: '100%' }}
                                value={ToYesNoEnum(
                                  tempFilterQuery.showPendingRejectedEvents
                                )}
                                valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                                onChange={(yesNoEnumValue) => {
                                  if (
                                    yesNoEnumValue !==
                                    ToYesNoEnum(
                                      tempFilterQuery.showPendingRejectedEvents
                                    )
                                  ) {
                                    setTempFilterQuery({
                                      ...tempFilterQuery,
                                      showPendingRejectedEvents:
                                        FromYesNoEnum(yesNoEnumValue),
                                    });
                                  }
                                }}
                              />
                            ),
                          },
                          {
                            filterId: 'eventDate' as FilterToolbarItemId,
                            labelContentId: ContentId.EventDate,
                            filterQueryKeys: [
                              'eventDate',
                            ] as unknown as FilterToolbarItemId[],
                            filterItem: (
                              <DateRangeSelector
                                presetNames={EventDateRangePresetNames}
                                value={dateFilter}
                                onBlur={({ value }) => {
                                  setDateFilter(value);
                                }}
                              />
                            ),
                          },
                        ],
                      },
                    ]}
                  />
                </Popover.Content>
              </Popover.Root>
            </div>
          </div>

          <PillList pills={pills} />
        </Stack>

        <div className={styles.buttonsContainer}>
          {hasResult && !isSingleSelect && (
            <Button variant="text" onClick={toggleSelectAll}>
              <Content
                id={isAllSelected ? ContentId.SelectNone : ContentId.SelectAll}
              />
            </Button>
          )}
          {hasResult && (
            <PosEnumSelect
              variant="outline"
              shape="pill"
              value={
                !isMobile
                  ? getEventUiSort(
                      eventSortQuery.sortBy,
                      eventSortQuery.isSortDescending
                    )
                  : null
              }
              defaultValue={
                isMobile ? null : getEventUiSort(EventSort.Date, true)
              }
              onChange={(sort) => {
                const [sortBy, isSortDescending] = getEventSort(sort);
                if (
                  sortBy !== eventSortQuery.sortBy ||
                  isSortDescending !== eventSortQuery.isSortDescending
                ) {
                  setEventSortQuery({
                    ...eventSortQuery,
                    sortBy,
                    isSortDescending,
                  });
                }
              }}
              valueOptionsContent={EVENT_SORT_TO_CID}
            />
          )}
        </div>
        <div className={styles.eventSearchResultContainer}>
          {!hasResult ? (
            searchEvents.isPending ? (
              <PosSpinner />
            ) : (
              <>
                <div data-testid="event-search-no-results">
                  <Content id={ContentId.SearchEmptyResult} />
                </div>
                {eventsToSearchFrom == null && (
                  <div>
                    <Button
                      variant="text"
                      data-testid="event-search-request-event"
                      onClick={() => {
                        if (onBeforeRequestEvent && !onBeforeRequestEvent()) {
                          return;
                        }
                        requestEventDialog.launchDialog();
                      }}
                    >
                      {requestCount != null && requestCount > 1 ? (
                        <FormatContent
                          id={FormatContentId.RequestNEvents}
                          params={[requestCount + '']}
                        />
                      ) : (
                        <Content id={ContentId.RequestEvent} />
                      )}
                    </Button>
                  </div>
                )}
              </>
            )
          ) : (
            <Virtuoso
              style={{ width: '100%' }}
              data={eventsToDisplay}
              overscan={window.innerHeight}
              itemContent={(i, d) => d}
            />
          )}
        </div>
      </Stack>
      {(requestCount == null || requestCount > 0) &&
        requestEventDialog.dialogProps.isOpen && (
          <RequestEventDialog
            {...requestEventDialog.dialogProps}
            onSave={onSaveRequestEvent}
            onCancel={requestEventDialog.closeDialog}
            requestCount={requestCount}
          />
        )}
    </>
  );
}
